import { getBrazilCivilDate } from './competence';

export const RECENT_BODY_PHOTOS_PERIODS = [
  'today',
  'last7',
  'last30',
  'custom',
] as const;

export type RecentBodyPhotosPeriod = (typeof RECENT_BODY_PHOTOS_PERIODS)[number];

const MAX_RANGE_DAYS = 366;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface RecentBodyPhotoAssessmentsQuery {
  dateStart: string;
  dateEnd: string;
  searchName: string;
  page: number;
  pageSize: number;
}

export type RecentBodyPhotoAssessmentsQueryResult =
  | { ok: true; query: RecentBodyPhotoAssessmentsQuery }
  | { ok: false; message: string };

export interface RecentBodyPhotoAssessmentRow {
  studentId: number;
  studentName: string;
  coachName: string | null;
  assessmentDate: string;
  uploadedAt: string;
  reviewedAt: string | null;
  reviewed: boolean;
}

export interface RecentBodyPhotoAssessmentsDashboard {
  page: number;
  pageSize: number;
  totalCount: number;
  assessments: RecentBodyPhotoAssessmentRow[];
}

/** Resolve query from optional date range. Empty dates = all folders. */
export function resolveRecentBodyPhotoAssessmentsQuery(
  dateStart: string,
  dateEnd: string,
  searchName: string,
  page: number,
  pageSize: number,
): RecentBodyPhotoAssessmentsQueryResult {
  const start = dateStart.trim();
  const end = dateEnd.trim();

  if (!start && !end) {
    return {
      ok: true,
      query: {
        dateStart: '',
        dateEnd: '',
        searchName,
        page,
        pageSize,
      },
    };
  }

  if (!ISO_DATE_RE.test(start) || !ISO_DATE_RE.test(end)) {
    return { ok: false, message: 'Informe a data inicial e a data final.' };
  }

  if (start > end) {
    return { ok: false, message: 'A data inicial não pode ser maior que a data final.' };
  }

  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  const days = Math.round((endMs - startMs) / 86_400_000);
  if (days > MAX_RANGE_DAYS) {
    return { ok: false, message: 'O período pode ter no máximo 366 dias.' };
  }

  return {
    ok: true,
    query: {
      dateStart: start,
      dateEnd: end,
      searchName,
      page,
      pageSize,
    },
  };
}

export function resolveRecentBodyPhotosPresetRange(
  period: Exclude<RecentBodyPhotosPeriod, 'custom'>,
  reference: Date = new Date(),
): { dateStart: string; dateEnd: string } {
  const today = getBrazilCivilDate(reference);

  if (period === 'today') {
    return { dateStart: today, dateEnd: today };
  }

  if (period === 'last7') {
    return {
      dateStart: getBrazilCivilDate(reference, -6),
      dateEnd: today,
    };
  }

  return {
    dateStart: getBrazilCivilDate(reference, -29),
    dateEnd: today,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readPositiveInt(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return null;
  }

  return value;
}

function readText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function readIsoTimestamp(value: unknown): string | null {
  const text = readText(value);
  if (!text) {
    return null;
  }

  return Number.isNaN(new Date(text).getTime()) ? null : text;
}

function parseRow(value: unknown): RecentBodyPhotoAssessmentRow | null {
  if (!isRecord(value)) {
    return null;
  }

  const studentId = readPositiveInt(value.studentId);
  const studentName = readText(value.studentName);
  const assessmentDate = readText(value.assessmentDate);
  const uploadedAt = readIsoTimestamp(value.uploadedAt);
  const reviewedAt = value.reviewedAt == null ? null : readIsoTimestamp(value.reviewedAt);
  const reviewed = value.reviewed === true;

  if (
    studentId === null
    || !studentName
    || !assessmentDate
    || !ISO_DATE_RE.test(assessmentDate)
    || !uploadedAt
    || (value.reviewedAt != null && !reviewedAt)
  ) {
    return null;
  }

  return {
    studentId,
    studentName,
    coachName: readText(value.coachName),
    assessmentDate,
    uploadedAt,
    reviewedAt,
    reviewed,
  };
}

export function parseRecentBodyPhotoAssessments(
  body: unknown,
): RecentBodyPhotoAssessmentsDashboard | null {
  if (!isRecord(body) || !Array.isArray(body.assessments) || typeof body.totalCount !== 'number') {
    return null;
  }

  const page = readPositiveInt(body.page) ?? 1;
  const pageSize = readPositiveInt(body.pageSize) ?? 20;
  const assessments = body.assessments
    .map((row) => parseRow(row))
    .filter((row): row is RecentBodyPhotoAssessmentRow => row !== null);

  if (!Number.isInteger(body.totalCount) || body.totalCount < 0) {
    return null;
  }

  return {
    page,
    pageSize,
    totalCount: body.totalCount,
    assessments,
  };
}

export function recentBodyPhotoAssessmentRowKey(row: Pick<RecentBodyPhotoAssessmentRow, 'studentId' | 'assessmentDate'>): string {
  return `${row.studentId}:${row.assessmentDate}`;
}
